import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  activatePersonService,
  changeEmployeeNumberService,
  changeInitialsService,
  changeLanguageService,
  changeNameService,
  deactivatePersonService,
  deletePersonService,
  fetchPersonsDetails,
  fetchPersonsOverview,
  updatePersonAdminStatus,
  updatePersonPassword,
} from '../services/personsService';

// async thunk
export const requestPersonsOverview = createAsyncThunk(
  'personEditor/requestPersonsOverview',
  async () => {
    const response = await fetchPersonsOverview();
    return response.data;
  }
);

export const requestPersonsDetails = createAsyncThunk(
  'personEditor/requestPersonsDetails',
  async (personId) => {
    const response = await fetchPersonsDetails(personId);
    return response.data;
  }
);

export const changePersonPassword = createAsyncThunk(
  'personEditor/changePersonPassword',
  async ({ personId, newPassword }) => {
    const response = await updatePersonPassword(personId, {
      password: newPassword,
    });
    return response.data;
  }
);

export const changePersonAdminState = createAsyncThunk(
  'personEditor/changePersonAdminState',
  async ({ personId, isAdmin }) => {
    const response = await updatePersonAdminStatus(personId, {
      isAdministrator: isAdmin,
    });
    return response.data;
  }
);

export const deactivatePerson = createAsyncThunk(
  'personEditor/deactivatePerson',
  async (personId, thunkAPI) => {
    const response = await deactivatePersonService(personId);
    await thunkAPI.dispatch(requestPersonsDetails(personId));
    return response.data;
  }
);

export const activatePerson = createAsyncThunk(
  'personEditor/activatePerson',
  async (personId) => {
    const response = await activatePersonService(personId);
    return response.data;
  }
);

export const deletePerson = createAsyncThunk(
  'personEditor/deletePerson',
  async (personId) => {
    const response = await deletePersonService(personId);
    return response.data;
  }
);

export const changeName = createAsyncThunk(
  'personEditor/changeName',
  async ({ personId, name }) => {
    const response = await changeNameService(personId, { name });
    return response.data;
  }
);

export const changeInitials = createAsyncThunk(
  'personEditor/changeInitials',
  async ({ personId, initials }) => {
    const response = await changeInitialsService(personId, { initials });
    return response.data;
  }
);
export const changeEmployeeNumber = createAsyncThunk(
  'personEditor/changeEmployeeNumber',
  async ({ personId, newEmployeeNumber }) => {
    const response = await changeEmployeeNumberService(personId, {
      employeeNumber: newEmployeeNumber,
    });
    return response.data;
  }
);

export const changeLanguage = createAsyncThunk(
  'personEditor/changeLanguage',
  async ({ personId, newLanguage }) => {
    const response = await changeLanguageService(personId, {
      locale: newLanguage,
    });
    return response.data;
  }
);

// slice
const personEditorSlice = createSlice({
  name: 'personEditor',
  initialState: {
    personsOverview: [],
    persons: {},
    locations: {},
    roles: {},
    locales: {},
    rootNodes: [],
    personsSortOrder: [],
    deactivatedPersonsSortOrder: [],
    changePasswordErrorMessage: null,
    isFetchingPersonsOverview: false,
    isFetchingPersonsDetails: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestPersonsOverview.pending, (state) => {
        state.isFetchingPersonsOverview = true;
      })
      .addCase(requestPersonsOverview.fulfilled, (state, action) => {
        state.isFetchingPersonsOverview = false;
        state.persons = action.payload.persons || {};
        state.personsOverview = action.payload;
        state.locations = action.payload.locations;
        state.roles = action.payload.roles;
        state.locales = action.payload.locales;
        state.rootNodes = action.payload.rootNodes;
        state.personsSortOrder = action.payload.personsSortOrder;
        state.deactivatedPersonsSortOrder =
          action.payload.deactivatedPersonsSortOrder;
      })
      .addCase(requestPersonsOverview.rejected, (state, action) => {
        state.isFetchingPersonsOverview = false;
      })
      .addCase(requestPersonsDetails.pending, (state) => {
        state.isFetchingPersonsDetails = true;
      })
      .addCase(requestPersonsDetails.fulfilled, (state, action) => {
        state.isFetchingPersonsDetails = false;
        const person = action.payload;
        const id = action.meta.arg;
        state.persons[id] = person;
        state.personsSortOrder = person.personsSortOrder;
        state.deactivatedPersonsSortOrder = person.deactivatedPersonsSortOrder;
      })
      .addCase(requestPersonsDetails.rejected, (state, action) => {
        state.isFetchingPersonsDetails = false;
      })
      .addCase(changePersonPassword.pending, (state) => {
        state.changePasswordErrorMessage = null;
      })
      .addCase(changePersonPassword.fulfilled, (state, action) => {
        const { personId, newPassword } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].password = newPassword;
        }
      })
      .addCase(changePersonPassword.rejected, (state, action) => {
        state.changePasswordErrorMessage = action.error.message;
      })
      .addCase(changePersonAdminState.pending, (state) => {
        state.changePasswordErrorMessage = null;
      })
      .addCase(changePersonAdminState.fulfilled, (state, action) => {
        const { personId, isAdmin } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].isAdmin = isAdmin;
        }
      })
      .addCase(changePersonAdminState.rejected, (state, action) => {
        state.changePasswordErrorMessage = action.error.message;
      })
      .addCase(deactivatePerson.pending, (state) => {
        console.log('Deactivating person...');
      })
      .addCase(deactivatePerson.fulfilled, (state, action) => {
        const personId = action.meta.arg;
        const payload = action.payload;
        if (state.persons[personId]) {
          state.persons[personId].isActive = false;
        }
        if (payload.personsSortOrder) {
          state.personsSortOrder = payload.personsSortOrder;
          state.deactivatedPersonsSortOrder =
            payload.deactivatedPersonsSortOrder;
        }
      })
      .addCase(deactivatePerson.rejected, (state, action) => {
        console.error('Failed to deactivate person:', action.error);
      })
      .addCase(activatePerson.pending, (state) => {
        console.log('Activating person...');
      })
      .addCase(activatePerson.fulfilled, (state, action) => {
        const { personId } = action.meta.arg;
        const payload = action.payload;
        if (state.persons[personId]) {
          state.persons[personId].isActive = true;
        }
        if (payload.personsSortOrder) {
          state.personsSortOrder = payload.personsSortOrder;
          state.deactivatedPersonsSortOrder =
            payload.deactivatedPersonsSortOrder;
        }
      })
      .addCase(activatePerson.rejected, (state, action) => {
        console.error('Failed to activate person:', action.error);
      })
      .addCase(deletePerson.pending, (state) => {
        console.log('Deleting person...');
      })
      .addCase(deletePerson.fulfilled, (state, action) => {
        const { personId } = action.meta.arg;
        const payload = action.payload;
        if (state.persons[personId]) {
          delete state.persons[personId];
        }
        if (payload.personsSortOrder) {
          state.personsSortOrder = payload.personsSortOrder;
          state.deactivatedPersonsSortOrder =
            payload.deactivatedPersonsSortOrder;
        }
      })
      .addCase(deletePerson.rejected, (state, action) => {
        console.error('Failed to delete person:', action.error);
      })
      .addCase(changeName.pending, (state) => {
        console.log('Changing person name...');
      })
      .addCase(changeName.fulfilled, (state, action) => {
        const { personId, name } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].name = name;
        }
      })
      .addCase(changeName.rejected, (state, action) => {
        console.error('Failed to change person name:', action.error);
      })
      .addCase(changeInitials.pending, (state) => {
        console.log('Changing person initials...');
      })
      .addCase(changeInitials.fulfilled, (state, action) => {
        const { personId, initials } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].initials = initials;
        }
      })
      .addCase(changeInitials.rejected, (state, action) => {
        console.error('Failed to change person initials:', action.error);
      })
      .addCase(changeEmployeeNumber.pending, (state) => {
        console.log('Changing person employee number...');
      })
      .addCase(changeEmployeeNumber.fulfilled, (state, action) => {
        const { personId, newEmployeeNumber } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].employeeNumber = newEmployeeNumber;
        }
      })
      .addCase(changeEmployeeNumber.rejected, (state, action) => {
        console.error('Failed to change person employee number:', action.error);
      })
      .addCase(changeLanguage.pending, (state) => {
        console.log('Changing person language...');
      })
      .addCase(changeLanguage.fulfilled, (state, action) => {
        const { personId, newLanguage } = action.meta.arg;
        if (state.persons[personId]) {
          state.persons[personId].language = newLanguage;
        }
      })
      .addCase(changeLanguage.rejected, (state, action) => {
        console.error('Failed to change person language:', action.error);
      });
  },
});

export default personEditorSlice.reducer;
